import {type StoreVerifyBuyerDetails} from '@square/web-payments-sdk-types';
import {Feature} from '../features';
import {$qs, loadScript} from '../../../@shared/ts/dom';
import {FeatureFlag} from '../../../@type/features';
import {onCheckoutSubmitManual, tokenizedMethodSelected, unblockCheckout} from '../checkout';

async function initSquareAddPaymentMethodPage($form: HTMLFormElement) {
	if (!Feature.enabled(FeatureFlag.SQUARE_GATEWAYS) || !Feature.enabled(FeatureFlag.SQUARE_CARD_GATEWAY)) {
		return;
	}

	const scriptSrc = Feature.metadata<string>(FeatureFlag.SQUARE_GATEWAYS, 'script_src');
	if (!scriptSrc) {
		return;
	}

	await loadScript(scriptSrc, 'Square');
	const applicationId = Feature.metadata<string>(FeatureFlag.SQUARE_GATEWAYS, 'application_id');
	const locationId = Feature.metadata<string>(FeatureFlag.SQUARE_GATEWAYS, 'location_id');

	if (!applicationId || !locationId || !window.Square) {
		return;
	}

	const payments = window.Square.payments(applicationId, locationId);
	const cardElement = await payments.card();
	const $container = $qs('#pp-square-card-element');

	if ($container) {
		await cardElement.attach($container);
	}

	// UI for adding new payment method for WC Subscriptions.
	const saveToAccountCheckbox = $qs('div.payment_method_peachpay_square_card p.woocommerce-SavedPaymentMethods-saveNew');
	if (saveToAccountCheckbox) {
		saveToAccountCheckbox.classList.add('hide');
	}

	const paymentMethodOptions = $qs<HTMLInputElement>('div.payment_method_peachpay_square_card ul.woocommerce-SavedPaymentMethods');
	const squareCardElement = $qs('div.payment_method_peachpay_square_card div.woocommerce-SavedPaymentMethods-saveNew');

	if (paymentMethodOptions && squareCardElement) {
		if (!$qs<HTMLInputElement>('div.payment_method_peachpay_square_card input#wc-peachpay_square_card-payment-token-new')?.checked) {
			squareCardElement.classList.add('hide');
		}

		paymentMethodOptions.addEventListener('change', event => {
			if ((event.target as HTMLInputElement).id === 'wc-peachpay_square_card-payment-token-new') {
				squareCardElement.classList.remove('hide');
			} else {
				squareCardElement.classList.add('hide');
			}
		});
	}

	onCheckoutSubmitManual(
		$form,
		['peachpay_square_card'],
		async (gatewayId: string) => {
			if (tokenizedMethodSelected(gatewayId)) {
				return true;
			}

			try {
				const tokenResult = await cardElement.tokenize();
				if (tokenResult.status !== 'OK' || !tokenResult.token) {
					return false;
				}

				const sourceId = tokenResult.token;

				const verifyDetails: StoreVerifyBuyerDetails = {
					intent: 'STORE',
					billingContact: {
						addressLines: [
							$qs<HTMLInputElement>('[name="billing_address_1"]')?.value ?? '',
							$qs<HTMLInputElement>('[name="billing_address_2"]')?.value ?? '',
						],
						email: $qs<HTMLInputElement>('[name="billing_email"]')?.value ?? '',
						familyName: $qs<HTMLInputElement>('[name="billing_last_name"]')?.value ?? '',
						givenName: $qs<HTMLInputElement>('[name="billing_first_name"]')?.value ?? '',
						city: $qs<HTMLInputElement>('[name="billing_city"]')?.value ?? '',
						countryCode: $qs<HTMLInputElement>('[name="billing_country"]')?.value ?? Feature.metadata<string>(FeatureFlag.SQUARE_GATEWAYS, 'country') ?? '',
						phone: $qs<HTMLInputElement>('[name="billing_phone"]')?.value ?? '',
						state: $qs<HTMLInputElement>('[name="billing_state"]')?.value ?? '',
						postalCode: $qs<HTMLInputElement>('[name="billing_postcode"]')?.value ?? '',
					},
				};

				const verifyResult = await payments.verifyBuyer(sourceId, verifyDetails);
				const verificationId = verifyResult?.token ?? null;

				insertTokenFields($form, sourceId, verificationId ?? undefined);
				return true;
			} catch (error) {
				unblockCheckout($form);
				return false;
			}
		},
	);
}

function insertTokenFields($form: HTMLFormElement, sourceId?: string, verificationId?: string) {
	if (sourceId) {
		const $sourceId = document.createElement('input');
		$sourceId.type = 'hidden';
		$sourceId.name = 'peachpay_square_source_id';
		$sourceId.value = sourceId;
		$form.insertAdjacentElement('beforeend', $sourceId);
	}

	if (verificationId) {
		const $verificationId = document.createElement('input');
		$verificationId.type = 'hidden';
		$verificationId.name = 'peachpay_square_verification_id';
		$verificationId.value = verificationId;
		$form.insertAdjacentElement('beforeend', $verificationId);
	}
}

export {initSquareAddPaymentMethodPage};
